import React, { useState, useEffect } from 'react';
import { COLORS } from '../../utils/constants';
import { ArrowUpCircle, ArrowDownCircle, Search, Filter, RefreshCw, Check, X, RotateCcw, Trash2, Eye, User, FileText } from 'lucide-react';
import { adminAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../context/PermissionsContext';
import { useAdminToast } from '../../hooks/useAdminToast';

const STATUS_COLORS = {
  pending: COLORS.primary,
  approved: COLORS.success,
  rejected: COLORS.danger,
  completed: COLORS.success,
  processing: COLORS.primary,
};

const STATUS_LABELS = {
  pending: 'Ожидает',
  approved: 'Одобрен',
  rejected: 'Отклонён',
  completed: 'Выполнен',
  processing: 'В обработке',
};

const AdminRequests = () => {
  const { hasPermission, isSuperAdmin } = usePermissions();
  const { toast } = useAdminToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [processing, setProcessing] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isSuperAdmin || hasPermission('requests', 'view')) {
      loadRequests();
    }
  }, [statusFilter, typeFilter, isSuperAdmin]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      const response = await adminAPI.getAllRequests(params);
      setRequests(response.data.requests || []);
    } catch (err) {
      console.error('Failed to load requests:', err);
    } finally {
      setLoading(false);
    }
  };

  // Access control - must be after all hooks
  if (!isSuperAdmin && !hasPermission('requests', 'view')) {
    return (
      <div style={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: COLORS.textSecondary 
      }}>
        <FileText size={64} style={{ marginBottom: '16px', opacity: 0.5 }} />
        <h2 style={{ color: COLORS.text, marginBottom: '8px' }}>Доступ запрещён</h2>
        <p>У вас нет прав для просмотра заявок</p>
      </div>
    );
  }

  const handleAction = async (request, action) => {
    if (!window.confirm(`${action === 'approve' ? 'Одобрить' : action === 'reject' ? 'Отклонить' : action === 'reset' ? 'Сбросить статус' : 'Удалить'} заявку?`)) return;
    
    setProcessing(request.id);
    try {
      if (request.type === 'deposit') {
        if (action === 'approve') {
          await adminAPI.approveDeposit(request.id, request.amount);
        } else if (action === 'reject') {
          await adminAPI.rejectDeposit(request.id, 'Отклонено администратором');
        } else if (action === 'reset') {
          await adminAPI.resetDepositStatus(request.id);
        } else if (action === 'delete') {
          await adminAPI.deleteDeposit(request.id);
        }
      } else {
        if (action === 'approve') {
          await adminAPI.processWithdrawal(request.id, 'approve');
        } else if (action === 'complete') {
          await adminAPI.processWithdrawal(request.id, 'complete');
        } else if (action === 'reject') {
          await adminAPI.processWithdrawal(request.id, 'reject', null, 'Отклонено администратором');
        } else if (action === 'reset') {
          await adminAPI.resetWithdrawalStatus(request.id);
        } else if (action === 'delete') {
          await adminAPI.deleteWithdrawal(request.id);
        }
      }
      loadRequests();
    } catch (err) {
      toast.apiError(err);
    } finally {
      setProcessing(null);
    }
  };

  const handleOpenClient = (userId) => {
    // Navigate to clients page with pre-selected client
    navigate(`/admin-panel/clients?client=${userId}`);
  };

  const filteredRequests = requests.filter(r => {
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        r.user_email?.toLowerCase().includes(searchLower) ||
        r.user_name?.toLowerCase().includes(searchLower) ||
        r.account_number?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ color: COLORS.text, fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
          Заявки на ввод/вывод
        </h1>
        <p style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
          Управление депозитами и выводами клиентов
        </p>
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: COLORS.textSecondary }} />
          <input
            type="text"
            placeholder="Поиск по email, имени, счёту..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 40px',
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.text,
              fontSize: '14px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Type Filter */}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{
            padding: '12px 16px',
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            color: COLORS.text,
            fontSize: '14px',
            minWidth: '150px',
          }}
        >
          <option value="">Все типы</option>
          <option value="deposit">Депозиты</option>
          <option value="withdrawal">Выводы</option>
        </select>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '12px 16px',
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            color: COLORS.text,
            fontSize: '14px',
            minWidth: '150px',
          }}
        >
          <option value="">Все статусы</option>
          <option value="pending">Ожидает</option>
          <option value="approved">Одобрен</option>
          <option value="completed">Выполнен</option>
          <option value="rejected">Отклонён</option>
          <option value="processing">В обработке</option>
        </select>

        {/* Refresh */}
        <button
          onClick={loadRequests}
          style={{
            padding: '12px',
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            color: COLORS.text,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '12px',
        marginBottom: '20px',
      }}>
        <StatCard
          label="Всего"
          value={requests.length}
          color={COLORS.text}
        />
        <StatCard
          label="Ожидает"
          value={requests.filter(r => r.status === 'pending').length}
          color={COLORS.primary}
        />
        <StatCard
          label="Депозиты"
          value={requests.filter(r => r.type === 'deposit').length}
          color={COLORS.success}
        />
        <StatCard
          label="Выводы"
          value={requests.filter(r => r.type === 'withdrawal').length}
          color={COLORS.danger}
        />
      </div>

      {/* Table */}
      <div style={{
        backgroundColor: COLORS.card,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden',
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '80px 2fr 1fr 1fr 1fr 200px',
          padding: '14px 20px',
          backgroundColor: COLORS.background,
          borderBottom: `1px solid ${COLORS.border}`,
          gap: '12px',
          alignItems: 'center',
        }}>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>ТИП</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>КЛИЕНТ</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>СУММА</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>СТАТУС</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600' }}>ДАТА</span>
          <span style={{ color: COLORS.textSecondary, fontSize: '12px', fontWeight: '600', textAlign: 'center' }}>ДЕЙСТВИЯ</span>
        </div>

        {/* Table Body */}
        <div style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>
              Загрузка...
            </div>
          ) : filteredRequests.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: COLORS.textSecondary }}>
              Заявки не найдены
            </div>
          ) : (
            filteredRequests.map((request) => (
              <RequestRow
                key={request.id}
                request={request}
                processing={processing}
                onAction={handleAction}
                onOpenClient={handleOpenClient}
                hasPermission={hasPermission}
                isSuperAdmin={isSuperAdmin}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div style={{
    padding: '16px',
    backgroundColor: COLORS.card,
    borderRadius: '8px',
    border: `1px solid ${COLORS.border}`,
  }}>
    <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>{label}</div>
    <div style={{ color, fontSize: '24px', fontWeight: '700' }}>{value}</div>
  </div>
);

const RequestRow = ({ request, processing, onAction, onOpenClient, hasPermission, isSuperAdmin }) => {
  const isDeposit = request.type === 'deposit';
  const isPending = request.status === 'pending';
  const isProcessing = request.status === 'processing';
  const canReset = request.status !== 'pending';
  
  // Permission checks
  const canApproveDeposit = isSuperAdmin || hasPermission('requests', 'approve_deposit');
  const canRejectDeposit = isSuperAdmin || hasPermission('requests', 'reject_deposit');
  const canApproveWithdrawal = isSuperAdmin || hasPermission('requests', 'approve_withdrawal');
  const canRejectWithdrawal = isSuperAdmin || hasPermission('requests', 'reject_withdrawal');

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '80px 2fr 1fr 1fr 1fr 200px',
      padding: '14px 20px',
      borderBottom: `1px solid ${COLORS.border}20`,
      gap: '12px',
      alignItems: 'center',
      backgroundColor: isPending ? `${COLORS.primary}05` : 'transparent',
    }}>
      {/* Type */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {isDeposit ? (
          <ArrowUpCircle size={20} color={COLORS.success} />
        ) : (
          <ArrowDownCircle size={20} color={COLORS.danger} />
        )}
        <span style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
          {isDeposit ? 'Депозит' : 'Вывод'}
        </span>
      </div>

      {/* Client - Clickable */}
      <div 
        onClick={() => onOpenClient(request.user_id)}
        style={{ cursor: 'pointer' }}
      >
        <div style={{ 
          color: COLORS.text, 
          fontSize: '14px', 
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          {request.user_name || request.user_email}
          <Eye size={14} color={COLORS.primary} />
        </div>
        <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
          {request.account_number}
        </div>
      </div>

      {/* Amount */}
      <div>
        <span style={{ 
          color: isDeposit ? COLORS.success : COLORS.danger, 
          fontWeight: '600',
          fontSize: '15px',
        }}>
          {isDeposit ? '+' : '-'}${request.amount}
        </span>
        <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
          {request.currency || 'USD'}
        </div>
      </div>

      {/* Status */}
      <div>
        <span style={{
          padding: '4px 10px',
          borderRadius: '4px',
          backgroundColor: `${STATUS_COLORS[request.status] || COLORS.textSecondary}20`,
          color: STATUS_COLORS[request.status] || COLORS.textSecondary,
          fontSize: '12px',
          fontWeight: '600',
        }}>
          {STATUS_LABELS[request.status] || request.status}
        </span>
      </div>

      {/* Date */}
      <div style={{ color: COLORS.textSecondary, fontSize: '13px' }}>
        {new Date(request.created_at).toLocaleString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
        {isPending && (
          <>
            {/* Approve button - check deposit/withdrawal permission */}
            {((isDeposit && canApproveDeposit) || (!isDeposit && canApproveWithdrawal)) && (
              <ActionBtn
                icon={Check}
                color={COLORS.success}
                title="Одобрить"
                onClick={() => onAction(request, 'approve')}
                disabled={processing === request.id}
              />
            )}
            {/* Reject button - check deposit/withdrawal permission */}
            {((isDeposit && canRejectDeposit) || (!isDeposit && canRejectWithdrawal)) && (
              <ActionBtn
                icon={X}
                color={COLORS.danger}
                title="Отклонить"
                onClick={() => onAction(request, 'reject')}
                disabled={processing === request.id}
              />
            )}
          </>
        )}
        {isProcessing && !isDeposit && canApproveWithdrawal && (
          <ActionBtn
            icon={Check}
            color={COLORS.success}
            title="Выполнить"
            onClick={() => onAction(request, 'complete')}
            disabled={processing === request.id}
          />
        )}
        {canReset && isSuperAdmin && (
          <ActionBtn
            icon={RotateCcw}
            color={COLORS.primary}
            title="Сбросить статус"
            onClick={() => onAction(request, 'reset')}
            disabled={processing === request.id}
          />
        )}
        {isSuperAdmin && (
          <ActionBtn
            icon={Trash2}
            color={COLORS.textSecondary}
            title="Удалить"
            onClick={() => onAction(request, 'delete')}
            disabled={processing === request.id}
          />
        )}
      </div>
    </div>
  );
};

const ActionBtn = ({ icon: Icon, color, title, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      padding: '6px',
      backgroundColor: `${color}15`,
      border: 'none',
      borderRadius: '4px',
      color: color,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Icon size={16} />
  </button>
);

export default AdminRequests;
